import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CountryCode } from '@carsys/enums/country-code'
import { Grid, Typography } from '@mui/material'
import { enumToOptions } from '@cbgms/base/utils/helpers'
import { isRequired } from '@cbgms/base/utils/validations'
import Dropdown from '@cbgms/components/form/Material/Dropdown'
import { Timezone } from '@carsys/enums/timezone'
import { TimeNotation } from '@carsys/enums/time-notation'
import { enumToLanguageOptions, localeOptionsByCountry, localeOptionsByCountrySupportedLanguages } from '@cbgms/base/translate/locales'
import RegionsForCountryDropdown from './RegionsForShopDropdown'
import { useFeature } from '@cbgms/base/feature-flags'
import { useIsPortal } from '../hooks/use-is-portal'

export interface IGeneralProps {
  country: CountryCode
}

export const Localization: React.FC<IGeneralProps> = props => {
  const hasAdditionalLanguageInShop = useFeature('additional-language-in-shop')
  const { t } = useTranslation()
  const timezoneOptions = enumToOptions(Timezone, 'EnumTimeZone')
  const timeNotationOptions = enumToOptions(TimeNotation, 'EnumTimeNotation')
  const isPortal = useIsPortal()

  const localeOptions = useMemo(() => {
    const locales = enumToLanguageOptions(
      hasAdditionalLanguageInShop && isPortal
        ? localeOptionsByCountrySupportedLanguages(props.country)
        : localeOptionsByCountry(props.country)
    )
    return locales
  }, [hasAdditionalLanguageInShop, isPortal, props.country])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant='h5'>{t('shop.localization')}</Typography>
      </Grid>
      <Grid item xs={4}>
        <Dropdown
          name='ShopLocalizationSettings.Timezone'
          label={t('user.timezone')}
          validate={[isRequired]}
          required
          fullWidth
          options={timezoneOptions}
        />
      </Grid>
      <Grid item xs={4}>
        <Dropdown
          name='ShopLocalizationSettings.TimeNotation'
          label={t('user.timenotation')}
          validate={[isRequired]}
          required
          fullWidth
          options={timeNotationOptions}
        />
      </Grid>
      <Grid item xs={4}>
        <Dropdown
          name='ShopLocalizationSettings.Locale'
          label={t('user.locale')}
          validate={[isRequired]}
          required
          fullWidth
          options={localeOptions}
        />
      </Grid>
      <Grid item xs={4}>
        <RegionsForCountryDropdown countryCode={props.country} fullWidth name='Shop.RegionUUID' data-testid='regions-dropdown' />
      </Grid>
    </Grid>
  )
}

export default Localization
